/**
 * SchedulePicker Component
 * Dialog for selecting a schedule to nest within another schedule.
 * Filters out the current schedule and schedules that would create circular references.
 */
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  InputAdornment,
  Grid,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarMonth as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Schedule, ScheduleType } from '../../types/schedule';

interface SchedulePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (schedule: Schedule) => void;
  schedules: Schedule[];
  currentScheduleId: string;  // Exclude current schedule to prevent self-reference
  loading?: boolean;
}

const TYPE_COLORS: Record<ScheduleType, string> = {
  routine: '#4caf50',
  daily_schedule: '#2196f3',
  first_then: '#ff9800',
  choice_board: '#9c27b0',
  waiting_board: '#607d8b',
  weekly_schedule: '#00bcd4',
};

export const SchedulePicker: React.FC<SchedulePickerProps> = ({
  open,
  onClose,
  onSelect,
  schedules,
  currentScheduleId,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter schedules: exclude current schedule and filter by search
  const filteredSchedules = useMemo(() => {
    return schedules
      .filter((s) => s.id !== currentScheduleId)  // Exclude self
      .filter((s) => s.isActive)  // Only show active schedules
      .filter((s) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          s.name.toLowerCase().includes(query) ||
          (s.description?.toLowerCase().includes(query) ?? false)
        );
      });
  }, [schedules, currentScheduleId, searchQuery]);

  const handleSelect = (schedule: Schedule) => {
    onSelect(schedule);
    setSearchQuery('');
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('schedules.schedulePicker.title')}</DialogTitle>
      <DialogContent>
        {/* Search */}
        <TextField
          fullWidth
          placeholder={t('schedules.schedulePicker.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Loading state */}
        {loading && (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={i}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Empty state */}
        {!loading && filteredSchedules.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <ScheduleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography>
              {searchQuery
                ? t('schedules.schedulePicker.noMatchingSchedules')
                : t('schedules.schedulePicker.noAvailableSchedules')}
            </Typography>
          </Box>
        )}

        {/* Schedule grid */}
        {!loading && filteredSchedules.length > 0 && (
          <Grid container spacing={2}>
            {filteredSchedules.map((schedule) => {
              const icon = schedule.icon?.emoji || '📋';
              const typeColor = TYPE_COLORS[schedule.type] || '#757575';
              const stepCount = schedule.steps?.length ?? 0;

              return (
                <Grid size={{ xs: 12, sm: 6 }} key={schedule.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <CardActionArea onClick={() => handleSelect(schedule)}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{ fontSize: '1.75rem' }}>{icon}</Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
                            {schedule.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              label={t(`schedules.types.${schedule.type}`)}
                              size="small"
                              sx={{
                                bgcolor: typeColor,
                                color: 'white',
                                height: 20,
                                '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {t('schedules.stepsCount', { count: stepCount })}
                            </Typography>
                          </Box>
                          {schedule.description && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {schedule.description}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};
